import { memo, useEffect, useState } from 'react'
import { Box, Grid, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import { useUpdateProductWarnings } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

interface WarningLine {
  en: string;
  ar: string;
}

// Stored as two lists in the same order, which is one list of warnings written
// twice - so it is read here as lines with an English and an Arabic side. A
// caution and its Arabic are one line, and editing one side alone is how the two
// stop lining up.
const fromProduct = (warnings: any): WarningLine[] => {
  const en: string[] = warnings?.en ?? [];
  const ar: string[] = warnings?.ar ?? [];
  const length = Math.max(en.length, ar.length);

  return Array.from({ length }, (_value, index) => ({
    en: en[index] ?? '',
    ar: ar[index] ?? '',
  }));
};

const toProduct = (lines: WarningLine[]) => {
  // A line blank in both languages is one nobody wrote; blank on one side only
  // is kept, because the pair is the unit.
  const written = lines.filter((line) => line.en.trim() || line.ar.trim());

  if (!written.length) return null;

  return {
    en: written.map((line) => line.en.trim()),
    ar: written.map((line) => line.ar.trim()),
  };
};

function WarningsDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [lines, setLines] = useState<WarningLine[]>([]);
  const [removingLine, setRemovingLine] = useState<number | null>(null);
  // Folded away until asked for, like the other long lists on this page.
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    setLines(fromProduct(product.warnings));
  }, [product.warnings]);

  const { mutate: updateMutation, isLoading } = useUpdateProductWarnings(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product warnings has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the warnings', 'error');
    },
  )

  const setLine = (index: number, language: 'en' | 'ar', text: string) =>
    setLines((previous) =>
      previous.map((line, i) => (i === index ? { ...line, [language]: text } : line)),
    );

  const handleSave = () => {
    updateMutation({ id: product.id, warnings: toProduct(lines) as any });
  }

  const handleCancel = () => {
    setLines(fromProduct(product.warnings));
    setIsEditing(false);
  }

  return (
    <Box mt={3}>
      <Section
        title="Product Warnings"
        subtitle={lines.length ? `${lines.length} printed` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isLoading,
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {lines.length === 0 && !isEditing && (
              <CustomText
                text={'This pack prints no warnings.'}
                fontSize={14}
                color={colors.gray}
              />
            )}

            <Stack gap={1.5}>
              {lines.map((line, index) => (
                <Stack key={index} direction="row" alignItems="flex-start" gap={1}>
                  <Box sx={{ pt: 1.25, minWidth: 24 }}>
                    <CustomText text={`${index + 1}.`} fontSize={14} color={colors.gray} />
                  </Box>

                  <Grid container spacing={1.5} flex={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        value={line.en}
                        disabled={!isEditing}
                        onChange={(event) => setLine(index, 'en', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        value={line.ar}
                        disabled={!isEditing}
                        onChange={(event) => setLine(index, 'ar', event.target.value)}
                        sx={{ '& textarea': { direction: 'rtl' } }}
                      />
                    </Grid>
                  </Grid>

                  {isEditing && (
                    <Box sx={{ pt: 0.5 }}>
                      <Tooltip title="Remove this warning">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRemovingLine(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Stack>
              ))}

              {isEditing && (
                <CustomButton
                  text={'Add Warning'}
                  variant={'outlined'}
                  startIcon={<AddIcon />}
                  height={40}
                  fullWidth
                  onClick={() => setLines((previous) => [...previous, { en: '', ar: '' }])}
                />
              )}
            </Stack>
          </Grid>
        }
      />

      <CustomDialog
        open={removingLine !== null}
        title={'Remove Warning'}
        message={'This removes the warning in both languages. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingLine(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              setLines((previous) => previous.filter((_line, i) => i !== removingLine));
              setRemovingLine(null);
            }}
          />,
        ]}
      />
    </Box>
  )
}

export default memo(WarningsDetails)
